/**
 * Orders Page
 * This page is intended for customers with a Point Of Sale (POS)
 * integration to view their orders.
 */
import { useState, useEffect } from 'react';
import { getOrderItemTimes } from '../../utils/api';
import { useData } from '../../hooks/useData';

import type { ChartData } from 'chart.js';
import { tailwindConfig } from '../../utils/Utils';

import DropdownFranchise from '../../components/dropdowns/DropdownFranchise';
import Datepicker from '../../components/time/Datepicker';
import DropdownLocationMultiple from '@/components/dropdowns/DropdownLocationMultiple';
import { OrderItemTime } from '@/types/OrderItemTime';
import HellometerLoading from './HellometerLoading';
import OrderItemTimesGraph from '@/components/graphs/orders/OrderItemTimesGraph';
import OrderItemTimesTable from '@/components/tables/OrderItemTimesTable';

export default function OrdersContainer(): JSX.Element {
  const [itemData, setItemData] = useState<OrderItemTime[]>([]);
  const [graphData, setGraphData] = useState<ChartData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [countFilterSetting, setCountFilterSetting] = useState<number>(0);
  const [useItemTime, setUseItemTime] = useState<boolean>(true);
  const [subtractAverageTime, setSubtractAverageTime] = useState<boolean>(false);
  const [maxCount, setMaxCount] = useState<number>(100);

  const {
    selectedFranchise
  } = useData();


  function handleRefresh(): void {
    fetchItemTimes().catch((err) => {
      console.log(err);
    });
  }


  // Fetch the daily orders.
  async function fetchItemTimes(): Promise<void> {
    if ( selectedFranchise === null) {
      return;
    }
    setIsLoading(true);

    getOrderItemTimes(
      selectedFranchise.id)
      .then((itemData) => {
        setItemData(itemData);
        setIsLoading(false);
        setGraphData(constructGraphData(itemData));
        setMaxCount(Math.max(...itemData.map((item) => item.count)));
      })
      .catch((error) => {
        console.error('Error fetching item time:', error);
        setIsLoading(false);
      });
  }

  function constructGraphData(orderItemTimes: OrderItemTime[], countFilter?: number, itemTime?: boolean = true, subtractTime?: boolean = false): ChartData {

    
    if (countFilter === undefined) {
      // determine countFilter by using mean of count 
      countFilter = orderItemTimes.map((item) => item.count).reduce((a, b) => a + b, 0) / orderItemTimes.length;

    }
    

    const itemsToUse = orderItemTimes.filter((item) => item.count >= countFilter);
    var sortedOrderItemTimes;
    var itemMap;
    
    if (itemTime)
    {
      // // calculate the (weighted) avereage orderItemTimeWithItem
    const { totalWeightedTime, totalCount } = itemsToUse.reduce(
      (acc, item) => ({
        // Sum of (value * weight)
        totalWeightedTime: acc.totalWeightedTime + item.itemTime * item.count,
        // Sum of all weights
        totalCount: acc.totalCount + item.count,
      }),
      { totalWeightedTime: 0, totalCount: 0 }
    );

    // Now, calculate the weighted average, handling division by zero.
    const weightedAverageItemTime = totalCount > 0
      ? totalWeightedTime / totalCount
      : 0;


    sortedOrderItemTimes = itemsToUse.sort(
      (a, b) => a.itemTime - b.itemTime
    );

    if (subtractTime){
        itemMap = itemsToUse.reduce((acc, item) => {
          acc[item.description] = item.itemTime-weightedAverageItemTime;
          return acc;
        }, {} as Record<string, number>);

        
      }
      else 
      {
        itemMap = itemsToUse.reduce((acc, item) => {
          acc[item.description] = item.itemTime;
          return acc;
        }, {} as Record<string, number>);

      }
    }
    else {
      // // calculate the (weighted) avereage orderItemTimeWithItem
      const { totalWeightedTime, totalCount } = itemsToUse.reduce(
        (acc, item) => ({
          // Sum of (value * weight)
          totalWeightedTime: acc.totalWeightedTime + item.orderTimeWithItem * item.count,
          // Sum of all weights
          totalCount: acc.totalCount + item.count,
        }),
        { totalWeightedTime: 0, totalCount: 0 }
      );

      // Now, calculate the weighted average, handling division by zero.
      const weightedAverageOrderTime = totalCount > 0
        ? totalWeightedTime / totalCount
        : 0;

      sortedOrderItemTimes = itemsToUse.sort(
        (a, b) => a.orderTimeWithItem - b.orderTimeWithItem
      );
      
      if (subtractTime){
        itemMap = itemsToUse.reduce((acc, item) => {
        acc[item.description] = item.orderTimeWithItem-weightedAverageOrderTime;
        return acc;
      }, {} as Record<string, number>);
        
      }
      else 
      {
        itemMap = itemsToUse.reduce((acc, item) => {
          acc[item.description] = item.orderTimeWithItem;
          return acc;
        }, {} as Record<string, number>);
      }
    }

    
    

    const chartData = {
      labels: sortedOrderItemTimes.map((item) => (item.description)),
      datasets: [
        {
          label: 'OrderItems',
          data: Object.values(itemMap),
          backgroundColor: (tailwindConfig().theme?.colors?.indigo as any)[500],
          hoverBackgroundColor: (
            tailwindConfig().theme?.colors?.indigo as any
          )[600],
          categoryPercentage: 0.66
        },
      ],
    };
    return chartData;
  }


  // Set available locations on franchise change.
  useEffect(() => {
    if (!selectedFranchise) {
      return;
    }
    
  }, [selectedFranchise]);

  useEffect(() => {
    if (!itemData) {
      return;
    }
    setGraphData(constructGraphData(itemData, countFilterSetting, useItemTime, subtractAverageTime));
  }, [countFilterSetting, useItemTime, subtractAverageTime]);

  useEffect(() => {
    if (selectedFranchise === null){
      return;
    }
    fetchItemTimes().catch((error) => {
      console.error('Error fetching daily orders:', error);
    });
    
  }, []);

  return (

      <div className="flex flex-col gap-4" data-cy="orders">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex flex-wrap gap-4">
            {/* Franchise Selector */}
            <DropdownFranchise />
            {/* Location Selector */}
          </div>
          
        </div>
        
      
      
      <> {isLoading  ?  <HellometerLoading/> :<>{graphData !== null && (
        <>
        <OrderItemTimesGraph
                    data={graphData}
                    handleRefresh={handleRefresh}
                    itemData={itemData}
                    countFilterSetting = {countFilterSetting}
                    maxCount = {maxCount}
                    setCountFilterSetting={setCountFilterSetting}
                    useItemTime={useItemTime}
                    setUseItemTime={setUseItemTime}
                    subtractAverageTime={subtractAverageTime}
                    setSubtractAverageTime={setSubtractAverageTime}
                  />
         <OrderItemTimesTable itemTimes={itemData} handleRefresh={handleRefresh} /></>
                  
                 

                )}</>}</>
       
       
      
      </div>

  );
}
